/**
 * ReporteAnexoVentasPage — Descarga del Anexo Detallado de Ventas para un
 * RANGO de fechas con filtro multi-selección de pasadías.
 *
 * Es el mismo Excel que se genera en el Cierre de Muelle (por día), pero
 * extendido a un rango. Layout: día → pasadía → reservas → subtotales.
 *
 * Backend:
 *   GET /turismo/informes/anexo-ventas-rango.xlsx?empresa_id&fecha_desde&fecha_hasta&paquete_ids
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, TextField, Button, Stack, Alert, Chip,
  FormControl, InputLabel, Select, MenuItem, OutlinedInput, Checkbox, ListItemText,
  Paper, Divider,
} from '@mui/material';
import TableChartIcon from '@mui/icons-material/TableChart';
import AssessmentIcon from '@mui/icons-material/Assessment';
import EventIcon from '@mui/icons-material/Event';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { API_BASE, getEmpresaId, getAuthHeaders } from '../booking/shared/format';

const todayISO = () => new Date().toISOString().slice(0, 10);
const firstDayOfMonthISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

// Umbral a partir del cual se muestra alerta amarilla — no bloquea la descarga
const DIAS_ALERTA = 90;

const menuItemStyle = { PaperProps: { style: { maxHeight: 380 } } };

export default function ReporteAnexoVentasPage() {
  const empresaId = getEmpresaId();

  const [fechaDesde, setFechaDesde] = useState(firstDayOfMonthISO());
  const [fechaHasta, setFechaHasta] = useState(todayISO());
  const [paqueteIds, setPaqueteIds] = useState([]); // multi-select
  const [paquetes, setPaquetes] = useState([]);
  const [error, setError] = useState(null);
  const [descargando, setDescargando] = useState(false);

  // Cargar paquetes disponibles para el filtro
  useEffect(() => {
    if (!empresaId) return;
    fetch(`${API_BASE}/turismo/paquetes?empresa_id=${empresaId}`, { headers: getAuthHeaders() })
      .then(r => (r.ok ? r.json() : []))
      .then(list => setPaquetes(Array.isArray(list) ? list.filter(p => p.activo !== false) : []))
      .catch(() => setPaquetes([]));
  }, [empresaId]);

  // Días del rango (inclusivo)
  const diasRango = useMemo(() => {
    if (!fechaDesde || !fechaHasta) return 0;
    const d1 = new Date(fechaDesde + 'T00:00:00');
    const d2 = new Date(fechaHasta + 'T00:00:00');
    return Math.floor((d2 - d1) / 86400000) + 1;
  }, [fechaDesde, fechaHasta]);

  const rangoInvalido = diasRango <= 0;
  const mostrarAlertaRango = diasRango > DIAS_ALERTA;

  const handleDescargar = async () => {
    if (!empresaId || rangoInvalido) return;
    setDescargando(true);
    setError(null);
    try {
      const qs = new URLSearchParams({
        empresa_id: empresaId,
        fecha_desde: fechaDesde,
        fecha_hasta: fechaHasta,
      });
      if (paqueteIds.length > 0) {
        qs.append('paquete_ids', paqueteIds.join(','));
      }

      const url = `${API_BASE}/turismo/informes/anexo-ventas-rango.xlsx?${qs}`;
      const resp = await fetch(url, { headers: getAuthHeaders() });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `Error ${resp.status}`);
      }
      const blob = await resp.blob();

      // Nombre del archivo según los filtros
      const rangoStr = fechaDesde === fechaHasta
        ? fechaDesde
        : `${fechaDesde}_${fechaHasta}`;
      const filtroStr = paqueteIds.length > 0 ? '_filtrado' : '';
      const filename = `anexo_ventas_${rangoStr}${filtroStr}.xlsx`;

      // Trigger descarga
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(link.href);
    } catch (e) {
      setError(e.message || 'Error al descargar el reporte');
    } finally {
      setDescargando(false);
    }
  };

  const paquetesSeleccionadosLabel = paqueteIds.length === 0
    ? 'Todas los pasadías'
    : paquetes
        .filter(p => paqueteIds.includes(p.id))
        .map(p => p.nombre)
        .join(' · ');

  const setRangoRapido = (dias) => {
    const hoy = new Date();
    const desde = new Date();
    desde.setDate(hoy.getDate() - dias + 1);
    setFechaDesde(desde.toISOString().slice(0, 10));
    setFechaHasta(hoy.toISOString().slice(0, 10));
  };

  const setMesActual = () => {
    setFechaDesde(firstDayOfMonthISO());
    setFechaHasta(todayISO());
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 900 }}>
      {/* Header */}
      <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 2 }}>
        <Box sx={{
          width: 44, height: 44, borderRadius: 2, bgcolor: '#16a34a15',
          display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#16a34a',
        }}>
          <TableChartIcon />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#14532d' }}>
            Anexo detallado de ventas
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#64748b' }}>
            Excel con todas las reservas del rango, agrupadas por día y pasadía, con
            subtotales y gran total. Excluye no-shows y cortesías.
          </Typography>
        </Box>
      </Stack>

      {/* Filtros */}
      <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid #e2e8f0' }}>
        <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#64748b',
            textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5,
            display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <FilterAltIcon sx={{ fontSize: 16 }} />
          Filtros del reporte
        </Typography>

        {/* Rango de fechas */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 1.5 }}>
          <TextField
            type="date" label="Desde" size="small"
            InputLabelProps={{ shrink: true }}
            value={fechaDesde}
            onChange={(e) => setFechaDesde(e.target.value)}
            sx={{ minWidth: 160 }}
          />
          <TextField
            type="date" label="Hasta" size="small"
            InputLabelProps={{ shrink: true }}
            value={fechaHasta}
            onChange={(e) => setFechaHasta(e.target.value)}
            sx={{ minWidth: 160 }}
          />
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button size="small" variant="text" onClick={() => setRangoRapido(7)}>7 días</Button>
            <Button size="small" variant="text" onClick={() => setRangoRapido(30)}>30 días</Button>
            <Button size="small" variant="text" onClick={setMesActual}>Mes actual</Button>
          </Box>
          <Box sx={{ flex: 1 }} />
          {diasRango > 0 && (
            <Chip
              size="small"
              icon={<EventIcon />}
              label={`${diasRango} día${diasRango !== 1 ? 's' : ''}`}
              sx={{ bgcolor: mostrarAlertaRango ? '#fef3c7' : '#dcfce7',
                    color: mostrarAlertaRango ? '#92400e' : '#166534',
                    fontWeight: 700 }}
            />
          )}
        </Stack>

        <Divider sx={{ my: 1.5 }} />

        {/* Multi-select de pasadías */}
        <FormControl fullWidth size="small" sx={{ mb: 1 }}>
          <InputLabel id="paquetes-label">Pasadías (dejar vacío = todos)</InputLabel>
          <Select
            labelId="paquetes-label"
            multiple
            value={paqueteIds}
            onChange={(e) => setPaqueteIds(
              typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value
            )}
            input={<OutlinedInput label="Pasadías (dejar vacío = todos)" />}
            renderValue={(selected) =>
              selected.length === 0
                ? 'Todos los pasadías'
                : `${selected.length} pasadía${selected.length !== 1 ? 's' : ''} seleccionado${selected.length !== 1 ? 's' : ''}`
            }
            MenuProps={menuItemStyle}
          >
            {paquetes.map(p => (
              <MenuItem key={p.id} value={p.id}>
                <Checkbox checked={paqueteIds.indexOf(p.id) > -1} />
                <ListItemText primary={p.nombre} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {paqueteIds.length > 0 && (
          <Typography sx={{ fontSize: 11.5, color: '#64748b', fontStyle: 'italic' }}>
            Filtrando: {paquetesSeleccionadosLabel}
          </Typography>
        )}
      </Paper>

      {/* Alerta rango > 90 días */}
      {mostrarAlertaRango && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          El rango seleccionado es de <strong>{diasRango} días</strong>. Los rangos
          mayores a {DIAS_ALERTA} días pueden generar archivos Excel grandes que
          tarden más en descargarse y abrirse. Puedes continuar; es solo un aviso.
        </Alert>
      )}

      {rangoInvalido && (
        <Alert severity="error" sx={{ mt: 2 }}>
          El rango de fechas es inválido — la fecha "hasta" debe ser igual o
          posterior a "desde".
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
      )}

      {/* Botón de descarga */}
      <Box sx={{ mt: 2.5, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          size="large"
          startIcon={<TableChartIcon />}
          onClick={handleDescargar}
          disabled={rangoInvalido || descargando}
          sx={{
            textTransform: 'none',
            background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
            boxShadow: '0 4px 12px rgba(22, 163, 74, 0.4)',
            fontWeight: 700,
            px: 3,
          }}
        >
          {descargando ? 'Generando...' : 'Descargar Excel'}
        </Button>
      </Box>

      {/* Notas al pie */}
      <Box sx={{ mt: 3, p: 1.5, bgcolor: '#f8fafc', borderRadius: 1.5, border: '1px solid #e2e8f0' }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#334155', mb: 0.5 }}>
          Estructura del Excel
        </Typography>
        <Typography sx={{ fontSize: 11.5, color: '#64748b', lineHeight: 1.6 }}>
          Una sola hoja plana con encabezado por día → agrupación por pasadía →
          filas de reservas → subtotal por pasadía → subtotal por día → gran total
          al final. Cada reserva ocupa una fila; los pagos se despliegan a la derecha
          en bloques &quot;PAGO 1&quot;, &quot;PAGO 2&quot;, etc. (fecha · valor · cuenta).
        </Typography>
      </Box>
    </Box>
  );
}
